/**
 * Mobile Alerts View
 * Shows in-app alerts with severity indicators
 */

import React, { useState, useEffect } from 'react';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  RefreshCw,
  Gauge,
  FileText,
  CheckSquare,
} from 'lucide-react';
import { mobileAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

function getSeverityStyle(severity) {
  switch (severity) {
    case 'crit':
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        icon: AlertCircle,
        iconColor: 'text-red-400',
        dot: 'bg-red-500',
      };
    case 'warn':
      return {
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/30',
        icon: AlertTriangle,
        iconColor: 'text-yellow-400',
        dot: 'bg-yellow-500',
      };
    default:
      return {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        icon: Info,
        iconColor: 'text-blue-400',
        dot: 'bg-blue-500',
      };
  }
}

function getTypeIcon(type) {
  switch (type) {
    case 'missingDoc':
    case 'docRejected':
    case 'docApproved':
      return FileText;
    case 'todoOverdue':
    case 'todoAssigned':
      return CheckSquare;
    default:
      return Gauge;
  }
}

export default function AlertsView() {
  const { fmt } = useFormat();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadAlerts = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const result = await mobileAPI.getAlerts();
      if (result.ok !== false) {
        setAlerts(result.items || []);
        setUnreadCount(result.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const markAsRead = async (alertId) => {
    try {
      await mobileAPI.markAlertRead(alertId);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, is_read: 1 } : a))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return fmt.date(date);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white">Alertes</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={() => loadAlerts(true)}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {/* Alerts List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Bell className="w-12 h-12 mb-2 opacity-50" />
            <p>Aucune alerte</p>
          </div>
        ) : (
          <ul className="p-4 space-y-3">
            {alerts.map((alert) => {
              const style = getSeverityStyle(alert.severity);
              const TypeIcon = getTypeIcon(alert.type);
              const SeverityIcon = style.icon;

              return (
                <li
                  key={alert.id}
                  onClick={() => !alert.is_read && markAsRead(alert.id)}
                  className={`rounded-xl border p-4 transition-colors cursor-pointer ${style.bg} ${style.border} ${
                    alert.is_read ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${style.bg}`}>
                      <SeverityIcon className={`w-5 h-5 ${style.iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white text-sm">
                          {alert.title}
                        </span>
                        {!alert.is_read && (
                          <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                        )}
                      </div>

                      {alert.message && (
                        <p className="text-sm text-slate-400 mb-2">{alert.message}</p>
                      )}

                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {/* Vehicle plate */}
                        {alert.vehicle_license_plate && (
                          <span className="flex items-center gap-1">
                            <Gauge className="w-3 h-3" />
                            {alert.vehicle_license_plate}
                          </span>
                        )}
                        {/* Time */}
                        <span>{formatDate(alert.created_at)}</span>
                      </div>
                    </div>

                    {/* Read indicator */}
                    {alert.is_read ? (
                      <Check className="w-5 h-5 text-slate-500" />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
